import { useMemo } from 'react';

import { TreeFolder } from './document-list-to-tree';

/**
 * Utility function for calculating summary counts
 * Handles both NS3451 (Norwegian) and OMUK (UK) folder structures
 *
 * @param entry - The folder entry to calculate count for
 * @param summaryCount - Record of folder IDs to document counts
 * @returns The total document count for the folder
 */
export const calculateSummaryCount = (entry: TreeFolder, summaryCount: Record<string, number>) => {
  // Special handling for "UNCATEGORIZED" - should only show documents that are actually uncategorized
  if (entry.id === 'UNCATEGORIZED') {
    return summaryCount?.[entry.id] || 0;
  }

  // Get the direct count for the current folder
  const directCount = summaryCount?.[entry.id] || 0;

  // Detect the format based on the entry ID
  const isNS3451 = entry.id.includes('NS3451');
  const isOMUK = entry.id.includes('OMUK');

  let childCount = 0;

  if (isNS3451) {
    childCount = Object.entries(summaryCount)
      .filter(([key]) => key.startsWith(entry.id) && key !== entry.id)
      .reduce((sum, [, count]) => sum + count, 0);
  } else if (isOMUK) {
    childCount = Object.entries(summaryCount)
      .filter(([key]) => key.startsWith(entry.id + '_'))
      .reduce((sum, [, count]) => sum + count, 0);
  }

  // Return the total count: current folder + child folders
  return directCount + childCount;
};

/**
 * Custom hook for managing summary count calculations
 * Handles both NS3451 (Norwegian) and OMUK (UK) folder structures
 *
 * @param entry - The folder entry to calculate count for
 * @param summaryCount - Record of folder IDs to document counts
 * @returns The total document count for the folder
 */
export const useSummaryCount = (entry: TreeFolder, summaryCount: Record<string, number>) => {
  return useMemo(() => {
    return calculateSummaryCount(entry, summaryCount);
  }, [entry, summaryCount]);
};
